import { useEffect, useMemo, useState } from "react";
import {
  getFormattedTimeStringFromPeriod,
  noBarData,
  noData,
  validLineData,
  type PerformanceFilter,
  horizontalBarChartMargins,
} from "../../../Library/Common/utils/performanceUtils";
import { reportingColors } from "features/Library/Common/utils/constants";
import { sharedGoogleReportingBarProps } from "features/Reporting/Google/utils/shared";
import { ChartWrapper, GroupedBarChart, HorizontalBarChart, LineChart } from "components/charts";
import CardReporting, { type CardReportingItem } from "components/cards/CardReporting/CardReporting";
import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";

import {
  sharedAccountReportingHorizontalBarProps,
  sharedAccountReportingLineProps,
} from "features/Reporting/Content/shared";
import { useGoogleTooltipFormats } from "features/Reporting/Google/utils/useGoogleTooltipFormats";
import { UserPerformanceList } from "./UserPerformanceList";

import { useChartPeriodMeasure } from "hooks/useChartPeriodMeasure";
import { useQuery } from "@tanstack/react-query";
import { type SelectedView, type UserAssetTypes } from "./types";
import { getActivityByTypeQuery, getLineChartQuery } from "./Queries/queries";

import styles from "./userPerformanceBody.module.scss";
import { FeatureFlags } from "featureFlags";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { type ConnectedProps, connect } from "react-redux";
import { bindAction } from "interfaces";
import { setHasDataToExport } from "features/Reporting/state/export/exportSlice";

export type ChartState = "line" | "column";

export type UserPerformanceBodyProps = PropsFromRedux & {
  filter: PerformanceFilter;
  activeTab: number;
  setActiveTab: (num: number) => void;
  userId: number;
  dateRange: Date[];
  handleContentSelection: (type: UserAssetTypes, view: SelectedView) => void;
};

const handleDropdownChange = (dropdownValue: string, setFunc: (newState: ChartState) => void) => {
  dropdownValue.indexOf("Bar") === -1 ? setFunc("line") : setFunc("column");
};

export const UserPerformanceBody: React.FC<UserPerformanceBodyProps> = ({
  filter,
  activeTab,
  setActiveTab,
  dateRange,
  userId,
  handleContentSelection,
  setHasExportData,
}) => {
  const [activityChartState, setActivityChartState] = useState<ChartState>("line");
  const reportEnabled = !!useFeatureFlag(FeatureFlags.UserPerformance);
  const [chartPeriod, measureRef] = useChartPeriodMeasure(
    dateRange,
    sharedAccountReportingLineProps.margins!.left + sharedAccountReportingLineProps.margins!.right,
  );
  const { groupedBarFormatter } = useGoogleTooltipFormats(chartPeriod, dateRange);

  const lineChartQuery = useQuery({
    queryKey: ["userCollaborationLine", filter, userId, chartPeriod],
    queryFn: getLineChartQuery,
    enabled: reportEnabled,
  });

  const activityByTypeQuery = useQuery({
    queryKey: ["userActivityByType", filter, userId],
    queryFn: getActivityByTypeQuery,
    enabled: reportEnabled,
  });

  useEffect(() => {
    setHasExportData({
      hasDataToExport:
        activityByTypeQuery.isSuccess &&
        (!!activityByTypeQuery.data.cards.platformOutreach || !!activityByTypeQuery.data.cards.userInteractions),
      tooltipContent: "No data available for export. This user contains no data.",
    });
  }, [setHasExportData, activityByTypeQuery.isSuccess, activityByTypeQuery.data]);

  const activeUsersChart = useMemo(() => {
    if (!lineChartQuery.data) {
      return <></>;
    }
    return activityChartState === "line" ? (
      <LineChart
        {...sharedAccountReportingLineProps}
        xData={[lineChartQuery.data.lineData.Date, lineChartQuery.data.lineData.Date]}
        yData={[lineChartQuery.data.lineData.platformOutreach, lineChartQuery.data.lineData.userInteractions]}
        xFormatterFunc={(d) => getFormattedTimeStringFromPeriod(d, chartPeriod, dateRange)}
        colorRange={[reportingColors.greenDark, reportingColors.blue]}
      />
    ) : (
      <GroupedBarChart
        {...sharedGoogleReportingBarProps}
        data={lineChartQuery.data.activityBarData}
        order={lineChartQuery.data.order}
        xTickFormatter={(d) => getFormattedTimeStringFromPeriod(d.toLocaleString(), chartPeriod, dateRange)}
        tooltipFormatter={groupedBarFormatter}
        colorRange={[reportingColors.greenDark, reportingColors.blue]}
      />
    );
  }, [lineChartQuery.data, activityChartState, groupedBarFormatter, chartPeriod, dateRange]);

  return (
    <div className={styles.root}>
      <div className={styles.activity} ref={measureRef}>
        <ChartWrapper
          titles={["Activity - Line Chart", "Activity - Bar Chart"]}
          legendLabels={["Platform Outreach", "Platform Interactions"]}
          showLegend
          onChange={(option) => handleDropdownChange(option, setActivityChartState)}
          colorRange={[reportingColors.greenDark, reportingColors.blue]}
        >
          <RequestStatusRenderer state={lineChartQuery.status}>
            {lineChartQuery.isSuccess &&
            validLineData([
              lineChartQuery.data.lineData.cumulativeUserInteractions,
              lineChartQuery.data.lineData.cumulativePlatformOutreach,
            ])
              ? activeUsersChart
              : noData(filter)}
          </RequestStatusRenderer>
        </ChartWrapper>
      </div>
      <div className={styles.activityByTypeQuery}>
        <ChartWrapper titles={["Activity By Interaction Type"]}>
          <RequestStatusRenderer state={activityByTypeQuery.status}>
            {activityByTypeQuery.isSuccess && !noBarData(...activityByTypeQuery.data.barData.map((d) => d.value)) ? (
              <HorizontalBarChart
                {...sharedAccountReportingHorizontalBarProps}
                countBarsToShowAtOnce={8}
                margins={{ ...horizontalBarChartMargins, left: 177 }}
                data={activityByTypeQuery.data.barData}
              />
            ) : (
              noData(filter)
            )}
          </RequestStatusRenderer>
        </ChartWrapper>
      </div>
      <div className={styles.cards}>
        <RequestStatusRenderer state={activityByTypeQuery.status}>
          {activityByTypeQuery.isSuccess && (
            <CardReporting
              items={
                [
                  {
                    statDescription: "Platform Outreach",
                    popupBody:
                      "The total outreach attempts, including Flows, emails, and messages made within the specified date range.",
                    stat: activityByTypeQuery.data.cards.platformOutreach,
                  },
                  {
                    statDescription: "Platform Interactions",
                    popupBody:
                      "All interactions by this user, including responses to outreaches and other activities, within the specified date range.",
                    stat: activityByTypeQuery.data.cards.userInteractions,
                  },
                  {
                    statDescription: "Groups",
                    popupBody: "The total groups this user belongs to.",
                    stat: activityByTypeQuery.data.cards.groups,
                  },
                  {
                    statDescription: "Licenses",
                    popupBody: "The total pack licenses issued to this user.",
                    stat: activityByTypeQuery.data.cards.licenses,
                  },
                ] as CardReportingItem[]
              }
            />
          )}
        </RequestStatusRenderer>
      </div>
      <div className={styles.table}>
        <UserPerformanceList
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          filter={filter}
          userId={userId}
          handleContentSelection={handleContentSelection}
        />
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  setHasExportData: bindAction(setHasDataToExport, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(UserPerformanceBody);
export default ConnectedComponent;
